import styles from './DoctorSelector.module.scss';

import * as React from 'react';

import { useSelector } from 'react-redux';
import { RootState } from '../../redux/types';

import { Label, NormalPeoplePicker, PersonaSize } from '@fluentui/react';
import { useCallback, useMemo } from 'react';

interface Props {
  value: string;
  onChange: (id: string) => void;
  label?: string;
  required?: boolean;
}

export const DoctorSelector = ({ value, onChange, label, required }: Props) => {
  const providers = useSelector((state: RootState) => state.providers.allProviders.data || []);

  const getSuggestions = useCallback(() => {
    return providers
      .filter((p) => p.providerType === 'DOCTOR' && p.active)
      .map((p) => ({
        text: `${p.prefix} ${p.firstName} ${p.lastName}`,
        secondaryText: p.email,
        imageUrl: p.avatarUrl,
        id: p.id,
        size: PersonaSize.size32,
      }));
  }, [providers]);

  const selectedItems = useMemo(() => {
    const provider = providers.find((p) => p.id === value);
    if (provider) {
      return [
        {
          text: `${provider.prefix} ${provider.firstName} ${provider.lastName}`,
          imageUrl: provider.avatarUrl,
          secondaryText: provider.email,
          id: provider.id,
        },
      ];
    }
    return [];
  }, [providers, value]);

  const onFilterChange = useCallback(
    (filterText: string) => {
      if (!filterText) {
        return [];
      }
      return providers
        .filter((p) => p.providerType === 'DOCTOR')
        .filter((p) => p.firstName.toLowerCase().indexOf(filterText.toLowerCase()) === 0 || p.lastName.toLowerCase().indexOf(filterText.toLowerCase()) === 0)
        .map((p) => ({
          text: `${p.prefix} ${p.firstName} ${p.lastName}`,
          imageUrl: p.avatarUrl,
          secondaryText: p.email,
          id: p.id,
        }));
    },
    [providers]
  );

  const onPickerChange = useCallback(
    (items?: any[]) => {
      if (items && items.length > 0) {
        onChange(items[0].id);
      } else {
        onChange('');
      }
    },
    [onChange]
  );

  return (
    <div>
      {label && <Label required={required}>{label}</Label>}
      <NormalPeoplePicker
        onResolveSuggestions={onFilterChange}
        onEmptyResolveSuggestions={getSuggestions}
        key="controlled"
        selectedItems={selectedItems}
        pickerSuggestionsProps={{ className: styles.item }}
        onChange={onPickerChange}
        itemLimit={2}
      />
    </div>
  );
};
